import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { createApiUrl } from '../utils/apiConfig';
import './BaseDashboard.css';

const BaseDashboard = ({ 
  title, 
  tabs, 
  user, 
  onLogout: _onLogout, 
  children,
  loading = false,
  defaultActiveTab,
  onTabChange,
  tabBadges = {}, 
  switchButton = null,
  banner = null
}) => {
  
  const [activeTab, setActiveTab] = useState(defaultActiveTab || tabs[0]?.id || 'default');
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Update activeTab when defaultActiveTab prop changes
  useEffect(() => {
    if (defaultActiveTab) {
      setActiveTab(defaultActiveTab);
    }
  }, [defaultActiveTab]);

  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  const handleTabChange = (tabId) => {
    // If onTabChange prop is provided, use it for custom tab handling
    if (onTabChange) {
      onTabChange(tabId);
    } else {
      // Default behavior
      setActiveTab(tabId);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(createApiUrl('/api/logout'), {}, {
        withCredentials: true
      });
      
      // Force a page reload to clear any cached state
      window.location.href = '/login';
    } catch (error) {
      console.error('Error logging out:', error);
      console.error('Error response:', error.response?.data);
      // Even if logout fails, redirect to login
      window.location.href = '/login';
    }
  };

  // Get the appropriate code for the subtitle
  const getUserCode = () => {
    // For school admins, show different codes based on current active role
    if (user.isSchAdmin) {
      if (user.currentActiveRole === 'teacher' && user.tchCode) {
        return { label: 'Teacher Code', value: user.tchCode };
      } else if (user.schoolCode) {
        // Check if it's a generated code (SCH_ prefix) or real school code from DB
        const isGeneratedCode = user.schoolCode.startsWith('SCH_');
        const displayValue = isGeneratedCode ? user.schoolCode : user.schoolCode;
        return { label: 'School Code', value: displayValue };
      } else if (user.tchCode) {
        return { label: 'Teacher Code', value: user.tchCode };
      }
    } else if ((user.isOrgAdmin || user.code) && (user.orgCode || user.code)) {
      const orgCodeValue = user.orgCode || user.code;
      return { label: 'Code', value: orgCodeValue };
    } else if (user.studentCode) {
      return { label: 'Code', value: user.studentCode };
    } else if (user.teacherCode || user.tchCode) {
      const teacherCodeValue = user.teacherCode || user.tchCode;
      return { label: 'Teacher Code', value: teacherCodeValue };
    }
    
    return { label: 'Code', value: 'Not available' };
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner-border text-light" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="base-dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">{title}</h1>
        <p className="dashboard-subtitle">
          Welcome back, {user.contactName} - {(() => {
            const codeObj = getUserCode();
            if (codeObj) {
              return `${codeObj.label}: ${codeObj.value}`;
            }
            return '';
          })()}
        </p>
      </div>

      {!isOnline && (
        <div className="alert alert-warning text-center">You are currently offline</div>
      )}

      {banner}

      <div className="dashboard-content-wrapper">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <ul className="nav nav-tabs">
            {tabs.map((tab) => (
              <li className="nav-item" key={tab.id}>
                <button 
                  className={`nav-link ${activeTab === tab.id ? 'active' : ''}`} 
                  onClick={() => handleTabChange(tab.id)}
                >
                  {tab.label}
                  {tabBadges[tab.id] && tabBadges[tab.id] > 0 && (
                    <span className="badge bg-danger ms-2">{tabBadges[tab.id]}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          <div className="d-flex align-items-center">
            {switchButton && (
              <div className="me-3">
                {switchButton}
              </div>
            )}
            <ul className="nav nav-tabs mb-0">
              <li className="nav-item">
                <button className="nav-link" onClick={handleLogout}>
                  Logout
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="tab-content">
          {children && React.Children.map(children, (child, _index) => {
            
            // Add null checks to prevent the error
            if (!child || !React.isValidElement(child)) {
              return null;
            }
            
            
            if (child.props?.tabId === activeTab) {
              return React.cloneElement(child, { 
                user, 
                isOnline,
                activeTab 
              });
            }
            return null;
          })}
        </div>
      </div>
    </div>
  );
};

export default BaseDashboard; 